// Utility functions for handling UTM parameters

const UTM_STORAGE_KEY = 'gebeya_utm_params';

// Utility function to get a cookie value by name
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null; // SSR safety
  }
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

// Get UTM parameters from cookies (primary method)
export const getUtmParamsFromCookies = (): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
} => {
  return {
    utm_source: getCookie('utm_source'),
    utm_medium: getCookie('utm_medium'),
    utm_campaign: getCookie('utm_campaign'),
    utm_content: getCookie('utm_content'),
    utm_term: getCookie('utm_term'),
    referrer: getCookie('referrer'),
  };
};

// Save UTM parameters to localStorage (keep for backward compatibility)
export const storeUtmParams = (
  utmSource?: string | null, 
  utmMedium?: string | null, 
  utmCampaign?: string | null, 
  utmContent?: string | null, 
  utmTerm?: string | null,
  referrer?: string | null
): void => {
  try {
    const utmParams = {
      utm_source: utmSource || null,
      utm_medium: utmMedium || null,
      utm_campaign: utmCampaign || null,
      utm_content: utmContent || null,
      utm_term: utmTerm || null,
      referrer: referrer || null
    };
    
    console.log('Storing UTM parameters in localStorage:', utmParams);
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  } catch (error) {
    console.error('Error storing UTM parameters in localStorage:', error);
  }
};

// Retrieve UTM parameters from cookies (primary) with localStorage fallback
export const getStoredUtmParams = (): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
} => {
  try {
    // First try to get from cookies
    const cookieParams = getUtmParamsFromCookies();
    
    // If we have any cookie data, use it
    if (cookieParams.utm_source || cookieParams.utm_medium || cookieParams.utm_campaign || 
        cookieParams.utm_content || cookieParams.utm_term || cookieParams.referrer) {
      console.log('Retrieved UTM parameters from cookies:', cookieParams);
      return cookieParams;
    }
    
    // Fallback to localStorage for backward compatibility
    const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
    if (storedParams) {
      console.log('Retrieved UTM parameters from localStorage fallback');
      return JSON.parse(storedParams);
    }
  } catch (error) {
    console.error('Error retrieving UTM parameters:', error);
  }
  
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    referrer: null
  };
};

// Clear UTM parameters from localStorage
export const clearUtmParams = (): void => {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing UTM parameters from localStorage:', error);
  }
}; 